import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, Platform, Pressable, ScrollView, Alert, TextInput, useWindowDimensions } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { XStack, YStack, H4, Paragraph } from 'tamagui';
import { MaterialCommunityIcons, AntDesign } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import useGlobalUser from '@hooks/getGlobalUser';
import apis from '@api/api';
import { useDispatch } from 'react-redux';
import { SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { getComponentById, getAllComponents } from '@registry/componentRegistry';
import { DataSourceSelector } from '@navigation/screens/MidLevel/EasyModeScreen/Components/DataSourceSelector';

interface DashboardComponent {
  componentId: string;
  componentType: string;
  config: any;
  dataMapping: any;
  position: { x: number; y: number };
  size: { width: number; height: number };
}

const DashboardEditorScreen = () => {
  const theme = themeSelector();
  const route = useRoute();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userId = useGlobalUser();
  const { width: screenWidth } = useWindowDimensions();
  const { dashboardId, mode = 'create' } = (route.params as any) || {};

  const [dashboardTitle, setDashboardTitle] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>([]);
  const [components, setComponents] = useState<DashboardComponent[]>([]);
  const [isEditing, setIsEditing] = useState(true);
  const [draggedComponent, setDraggedComponent] = useState<string | null>(null);
  const [showDataSourceSelector, setShowDataSourceSelector] = useState(mode === 'create');

  // Load dashboard if editing
  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard', dashboardId],
    queryFn: async () => {
      if (!dashboardId) return null;
      return await apis.getDashboard(dashboardId);
    },
    enabled: !!dashboardId && mode === 'edit',
    onSuccess: (data) => {
      if (data?.data) {
        const dashboard = data.data;
        setDashboardTitle(dashboard.title);
        setDashboardDescription(dashboard.description || '');
        setSelectedDataSources(dashboard.dataSourceIds || []);
        setComponents(dashboard.components || []);
      }
    }
  });

  // Fetch available data sources
  const { data: dataSources } = useQuery({
    queryKey: ['dataSources', userId],
    queryFn: async () => {
      if (!userId) return [];
      return await apis.getDataFolders({ userId: userId, dataFilter: 'all' });
    },
    enabled: !!userId,
  });

  const handleDataSourceSelection = useCallback(async (selectedIds: string[]) => {
    if (selectedIds.length === 0) return;

    setSelectedDataSources(selectedIds);
    setShowDataSourceSelector(false);

    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Analyzing data sources...' } });
      
      const response = await apis.suggestDashboardComponents(selectedIds);
      
      dispatch({ type: HIDE_LOADING_MODAL });

      if (response?.success && response?.components) {
        const suggestedComponents = response.components.map((comp: any, index: number) => ({
          componentId: comp.componentId,
          componentType: comp.componentType,
          config: comp.config || {},
          dataMapping: comp.dataMapping || {},
          position: { x: (index % 2) * 50, y: Math.floor(index / 2) * 200 },
          size: { width: 48, height: 200 }
        }));
        
        setComponents(suggestedComponents);
      }
    } catch (error: any) {
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to analyze data: ${error.message}`, type: 'error' } });
    }
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    if (!dashboardTitle.trim()) {
      Alert.alert('Title Required', 'Please enter a title for your dashboard');
      return;
    }

    if (selectedDataSources.length === 0) {
      Alert.alert('Data Sources Required', 'Please select at least one data source');
      return;
    }

    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Saving dashboard...' } });

      const dashboardData = {
        title: dashboardTitle.trim(),
        description: dashboardDescription.trim(),
        dataSourceIds: selectedDataSources,
        layout: {
          type: 'grid',
          columns: 2,
          spacing: 16
        },
        components: components.map(comp => ({
          componentId: comp.componentId,
          componentType: comp.componentType,
          config: comp.config,
          dataMapping: comp.dataMapping,
          position: comp.position,
          size: comp.size,
        })),
      };

      let response;
      if (mode === 'edit' && dashboardId) {
        response = await apis.updateDashboard(dashboardId, dashboardData);
      } else {
        response = await apis.createDashboard(dashboardData);
      }
      
      dispatch({ type: HIDE_LOADING_MODAL });

      if (response?.success) {
        dispatch({ 
          type: SHOW_AUTH_MODAL, 
          data: { content: `Dashboard ${mode === 'edit' ? 'updated' : 'created'} successfully!`, type: 'success' } 
        });
        queryClient.invalidateQueries({ queryKey: ['userDashboards'] });
        navigation.goBack();
      } else {
        throw new Error(response?.error || 'Failed to save dashboard');
      }
    } catch (error: any) {
      console.error('Error saving dashboard:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ 
        type: SHOW_AUTH_MODAL, 
        data: { content: `Failed to save dashboard: ${error.message}`, type: 'error' } 
      });
    }
  }, [dashboardTitle, dashboardDescription, selectedDataSources, components, mode, dashboardId, dispatch, navigation, queryClient]);

  const renderComponent = useCallback((component: DashboardComponent, index: number) => {
    const componentEntry = getComponentById(component.componentId);
    
    if (!componentEntry) {
      return (
        <View
          key={index}
          style={{
            backgroundColor: cardColors.surface,
            borderRadius: 8,
            padding: 16,
            margin: 8,
            borderWidth: 1,
            borderColor: cardColors.border,
            minHeight: 200,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text style={{ color: cardColors.textSecondary, fontSize: 12 }}>
            Component {component.componentId} not available
          </Text>
        </View>
      );
    }

    const Component = componentEntry.component;
    const componentData = {
      ...component.config,
      ...component.dataMapping,
    };

    return (
      <View
        key={`${component.componentId}-${index}`}
        style={{
          backgroundColor: cardColors.surface,
          borderRadius: 8,
          padding: 12,
          margin: 8,
          borderWidth: 2,
          borderColor: draggedComponent === component.componentId ? cardColors.accent : cardColors.border,
          minHeight: component.size?.height || 200,
          width: component.size?.width ? `${component.size.width}%` : '48%',
          position: 'relative',
        }}
      >
        {isEditing && (
          <Pressable
            onPress={() => {
              setComponents(prev => prev.filter((_, i) => i !== index));
            }}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: '#ef4444',
              borderRadius: 12,
              width: 24,
              height: 24,
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            <AntDesign name="close" size={14} color="white" />
          </Pressable>
        )}
        
        <Component data={componentData} />
      </View>
    );
  }, [isEditing, draggedComponent]);

  const cardColors = {
    background: theme ? '#ffffff' : '#374151',
    border: theme ? '#e5e7eb' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    accent: '#3b82f6',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
  };

  if (isLoading) {
    return (
      <DefaultScreen>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ color: cardColors.text }}>Loading dashboard...</Text>
        </View>
      </DefaultScreen>
    );
  }

  return (
    <DefaultScreen>
      <View style={{ flex: 1, backgroundColor: theme ? '#f9fafb' : '#1f2937' }}>
        {/* Header */}
        <View style={{
          padding: 16,
          backgroundColor: cardColors.background,
          borderBottomWidth: 1,
          borderBottomColor: cardColors.border,
        }}>
          <XStack alignItems="center" justifyContent="space-between" marginBottom={16}>
            <H4 color={cardColors.text}>
              {mode === 'edit' ? 'Edit Dashboard' : mode === 'view' ? 'View Dashboard' : 'Create Dashboard'}
            </H4>
            <XStack gap={8}>
              {mode !== 'view' && (
                <Pressable
                  onPress={() => setIsEditing(!isEditing)}
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    backgroundColor: isEditing ? cardColors.accent : cardColors.surface,
                    borderRadius: 6,
                  }}
                >
                  <Text style={{ color: isEditing ? 'white' : cardColors.text, fontSize: 12, fontWeight: '600' }}>
                    {isEditing ? 'Done Editing' : 'Edit Layout'}
                  </Text>
                </Pressable>
              )}
              <Pressable
                onPress={() => navigation.goBack()}
                style={{
                  padding: 8,
                  borderRadius: 6,
                  backgroundColor: cardColors.surface,
                }}
              >
                <AntDesign name="close" size={20} color={cardColors.text} />
              </Pressable>
            </XStack>
          </XStack>

          {/* Title and Description Inputs */}
          {mode !== 'view' && (
            <YStack gap={12}>
              <YStack>
                <Text style={{ fontSize: 14, color: cardColors.text, marginBottom: 4 }}>Title</Text>
                <TextInput
                  value={dashboardTitle}
                  onChangeText={setDashboardTitle}
                  placeholder="Enter dashboard title..."
                  placeholderTextColor={cardColors.textSecondary}
                  style={{
                    backgroundColor: cardColors.surface,
                    borderRadius: 8,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: cardColors.border,
                    color: cardColors.text,
                    fontSize: 14,
                  }}
                />
              </YStack>
              <YStack>
                <Text style={{ fontSize: 14, color: cardColors.text, marginBottom: 4 }}>Description (Optional)</Text>
                <TextInput
                  value={dashboardDescription}
                  onChangeText={setDashboardDescription}
                  placeholder="Enter dashboard description..."
                  placeholderTextColor={cardColors.textSecondary}
                  multiline
                  numberOfLines={2}
                  style={{
                    backgroundColor: cardColors.surface,
                    borderRadius: 8,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: cardColors.border,
                    color: cardColors.text,
                    fontSize: 14,
                    minHeight: 60,
                  }}
                />
              </YStack>
            </YStack>
          )}
        </View>

        {/* Data Source Selector */}
        {showDataSourceSelector && dataSources && (
          <View style={{ padding: 16, backgroundColor: cardColors.background }}>
            <DataSourceSelector
              dataSources={dataSources}
              selectedIds={selectedDataSources}
              theme={theme}
              onSelectionChange={setSelectedDataSources}
              onConfirm={handleDataSourceSelection}
            />
          </View>
        )}

        {/* Dashboard Preview */}
        {components.length > 0 && (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              padding: 16,
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
            }}
          >
            {components.map((component, index) => renderComponent(component, index))}
          </ScrollView>
        )}

        {/* Empty State */}
        {components.length === 0 && !showDataSourceSelector && (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
            <MaterialCommunityIcons name="view-dashboard-outline" size={64} color={cardColors.textSecondary} />
            <Text style={{ color: cardColors.text, fontSize: 18, fontWeight: '600', marginTop: 16, marginBottom: 8 }}>
              No components yet
            </Text>
            <Text style={{ color: cardColors.textSecondary, fontSize: 14, textAlign: 'center', marginBottom: 24 }}>
              Select data sources to generate dashboard components.
            </Text>
            <Pressable
              onPress={() => setShowDataSourceSelector(true)}
              style={{
                paddingHorizontal: 24,
                paddingVertical: 12,
                borderRadius: 8,
                backgroundColor: cardColors.accent,
              }}
            >
              <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
                Select Data Sources
              </Text>
            </Pressable>
          </View>
        )}

        {/* Save Button */}
        {mode !== 'view' && (
          <View style={{
            padding: 16,
            backgroundColor: cardColors.background,
            borderTopWidth: 1,
            borderTopColor: cardColors.border,
          }}>
            <XStack gap={12} justifyContent="flex-end">
              <Pressable
                onPress={() => navigation.goBack()}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  backgroundColor: cardColors.surface,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: cardColors.border,
                }}
              >
                <Text style={{ color: cardColors.text, fontSize: 14, fontWeight: '600' }}>
                  Cancel
                </Text>
              </Pressable>
              <Pressable
                onPress={handleSave}
                disabled={!dashboardTitle.trim() || selectedDataSources.length === 0}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  backgroundColor: (dashboardTitle.trim() && selectedDataSources.length > 0) ? cardColors.accent : cardColors.textSecondary,
                  borderRadius: 8,
                  opacity: (dashboardTitle.trim() && selectedDataSources.length > 0) ? 1 : 0.5,
                }}
              >
                <Text style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                  {mode === 'edit' ? 'Update Dashboard' : 'Save Dashboard'}
                </Text>
              </Pressable>
            </XStack>
          </View>
        )}
      </View>
    </DefaultScreen>
  );
};

export default DashboardEditorScreen;






